import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  ArrowsClockwise, MagnifyingGlass, CaretRight, CaretDown,
  ListBullets, Horse, Wrench, Trophy, Target,
} from '@phosphor-icons/react';
import api from '../../utils/api';

/**
 * RiderChargesTab — itemized per-rider statement for Manage Entries.
 *
 * Each rider row collapses to a clean summary (name, horse, paid/pending
 * totals) and expands into every line item they were charged across the
 * show: classes, sidepots, target race, add-ons, stalls/RV.
 */
export const RiderChargesTab = ({ showId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL'); // ALL | PENDING | PAID
  const [expanded, setExpanded] = useState(() => new Set());
  const [repairing, setRepairing] = useState(false);

  const handleRepair = async () => {
    if (!window.confirm(
      "Repair Multi-Go siblings created before v146?\n\n"
      + "This finds every '(Multi-Go sibling)' entry whose lowest-go partner "
      + "is missing, and converts the lowest-go entry back into a parent so "
      + "the rider's totals re-include its entry fee.\n\n"
      + "Safe to run — only modifies entries created by the old buggy expansion."
    )) return;
    setRepairing(true);
    try {
      const { data: res } = await api.post(`/shows/${showId}/rider-charges/repair-multi-go`);
      toast.success(`Repaired ${res.repaired} parent entries across ${res.riders_touched} rider/horse combos`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Repair failed');
    } finally { setRepairing(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/shows/${showId}/rider-charges`);
      setData(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load rider charges');
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (idx) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!data?.riders) return [];
    const term = search.trim().toLowerCase();
    return data.riders.filter((r) => {
      if (filter === 'PENDING' && r.pending_total <= 0) return false;
      if (filter === 'PAID' && r.pending_total > 0) return false;
      if (!term) return true;
      return r.rider_name.toLowerCase().includes(term)
        || (r.horse_name || '').toLowerCase().includes(term)
        || (r.email || '').toLowerCase().includes(term);
    });
  }, [data, search, filter]);

  if (loading && !data) {
    return <div className="py-8 text-center text-[#9E9E9E] text-sm">Loading…</div>;
  }
  if (!data) return null;

  const { summary } = data;
  const sidepots = summary.sidepots || { total_count: 0, pending_count: 0, paid_count: 0, pending_revenue: 0, paid_revenue: 0, total_revenue: 0 };
  const target = summary.target_race || { total_count: 0, pending_count: 0, paid_count: 0, pending_revenue: 0, paid_revenue: 0, total_revenue: 0 };

  return (
    <div data-testid="rider-charges-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <StatCard label="Riders" value={summary.rider_count} color="#9E9E9E" testid="rc-stat-riders" />
        <StatCard label="Paid" value={`$${summary.paid_total.toFixed(2)}`} color="#34C759" testid="rc-stat-paid" />
        <StatCard label="Pending" value={`$${summary.pending_total.toFixed(2)}`} color="#FF9500" testid="rc-stat-pending" />
        <StatCard label="Grand Total" value={`$${summary.grand_total.toFixed(2)}`} color="#FF3B30" testid="rc-stat-grand" />
      </div>

      {/* Sidepots + Target Race breakdown — pending visibility for the producer */}
      {(sidepots.total_count > 0 || target.total_count > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <CategoryBreakdownCard
            icon={Trophy}
            label="Sidepots Selected"
            data={sidepots}
            color="#AF52DE"
            testid="rc-cat-sidepots"
          />
          <CategoryBreakdownCard
            icon={Target}
            label="Target Race Selected"
            data={target}
            color="#FF2D55"
            testid="rc-cat-target"
          />
        </div>
      )}

      {/* Search + filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9E9E]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rider or horse…"
            className="w-full pl-8 pr-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#FF9500]"
            data-testid="rc-search"
          />
        </div>
        <div className="flex border border-[#262626] rounded overflow-hidden">
          {['ALL', 'PENDING', 'PAID'].map((opt) => (
            <button
              key={opt}
              onClick={() => setFilter(opt)}
              className={`px-3 py-2 text-[10px] font-bold uppercase transition-colors ${
                filter === opt
                  ? 'bg-[#FF9500] text-black'
                  : 'bg-[#0A0A0A] text-[#9E9E9E] hover:text-white'
              }`}
              data-testid={`rc-filter-${opt.toLowerCase()}`}
            >
              {opt}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="px-2.5 py-2 bg-[#0A0A0A] border border-[#262626] hover:border-[#FF9500] text-[#9E9E9E] hover:text-white rounded transition-colors"
          title="Refresh"
          data-testid="rc-refresh"
        >
          <ArrowsClockwise size={12} />
        </button>
        <button
          onClick={handleRepair}
          disabled={repairing}
          className="px-3 py-2 bg-transparent border border-[#FF9500] hover:bg-[#FF9500]/10 text-[#FF9500] text-[10px] font-bold rounded transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
          title="Convert legacy $0 Multi-Go siblings back to parents (post v146 fix)"
          data-testid="rc-repair-multigo"
        >
          <Wrench size={12} weight="bold" />
          {repairing ? 'Repairing…' : 'Repair Multi-Go'}
        </button>
      </div>

      {/* Per-rider table */}
      {filtered.length === 0 ? (
        <div className="py-12 text-center bg-[#0A0A0A] border border-dashed border-[#262626] rounded-lg" data-testid="rc-empty">
          <ListBullets size={32} className="text-[#262626] mx-auto mb-2" />
          <p className="text-sm text-[#9E9E9E]">No rider charges match.</p>
        </div>
      ) : (
        <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded overflow-hidden" data-testid="rc-table">
          {filtered.map((r, idx) => {
            const isOpen = expanded.has(idx);
            return (
              <div key={`${r.rider_name}-${r.horse_name}-${idx}`} className="border-t border-[#1F1F1F] first:border-t-0" data-testid={`rc-row-${idx}`}>
                <button
                  onClick={() => toggle(idx)}
                  className="w-full px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-[#161616] transition-colors text-left"
                  data-testid={`rc-toggle-${idx}`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isOpen ? <CaretDown size={14} className="text-[#9E9E9E] flex-shrink-0" /> : <CaretRight size={14} className="text-[#9E9E9E] flex-shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white truncate">{r.rider_name}</div>
                      {(r.horse_name || r.email) && (
                        <div className="text-[10px] text-[#9E9E9E] truncate flex items-center gap-2">
                          {r.horse_name && <span className="inline-flex items-center gap-1"><Horse size={10} /> {r.horse_name}</span>}
                          {r.email && <span>· {r.email}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.pending_total > 0 ? (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#FF9500]/15 text-[#FF9500] whitespace-nowrap">
                        ${r.pending_total.toFixed(2)} due
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#34C759]/15 text-[#34C759] whitespace-nowrap">
                        Paid in full
                      </span>
                    )}
                    <span className="text-base font-bold text-white tabular-nums whitespace-nowrap" data-testid={`rc-grand-${idx}`}>
                      ${r.grand_total.toFixed(2)}
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 bg-[#0F0F0F]" data-testid={`rc-detail-${idx}`}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase text-[#9E9E9E]">
                          <th className="px-2 py-1.5 font-semibold text-left">Item</th>
                          <th className="px-2 py-1.5 font-semibold text-right">Qty</th>
                          <th className="px-2 py-1.5 font-semibold text-right">Amount</th>
                          <th className="px-2 py-1.5 font-semibold text-right">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.items.map((it, i) => (
                          <tr key={i} className="border-t border-[#1A1A1A]">
                            <td className="px-2 py-1.5">
                              <span className={`inline-block w-1 h-1 rounded-full mr-2 align-middle`} style={{ background: CATEGORY_COLORS[it.category] || '#9E9E9E' }} />
                              <span className="text-white">{it.label}</span>
                            </td>
                            <td className="px-2 py-1.5 text-right text-[#9E9E9E]">{it.qty}</td>
                            <td className="px-2 py-1.5 text-right text-white font-mono">${Number(it.amount || 0).toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right">
                              <PaymentBadge status={it.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-[#1F1F1F]">
                          <td colSpan={2} className="px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-[#9E9E9E]">Paid</td>
                          <td className="px-2 py-1.5 text-right font-bold text-[#34C759]">${r.paid_total.toFixed(2)}</td>
                          <td />
                        </tr>
                        <tr>
                          <td colSpan={2} className="px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-[#9E9E9E]">Pending</td>
                          <td className="px-2 py-1.5 text-right font-bold text-[#FF9500]">${r.pending_total.toFixed(2)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CATEGORY_COLORS = {
  class: '#FF9500',
  sidepot: '#0096FF',
  target: '#AF52DE',
  addon: '#34C759',
  stall: '#FACC15',
  rv: '#5E5CE6',
  office_fee: '#FF3B30',
};

const StatCard = ({ label, value, color, testid }) => (
  <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded p-3" data-testid={testid}>
    <p className="text-[9px] uppercase tracking-wider text-[#9E9E9E]">{label}</p>
    <p className="text-2xl font-black text-white mt-1" style={{ fontFamily: 'Outfit', color }}>{value}</p>
  </div>
);

const CategoryBreakdownCard = ({ icon: Icon, label, data, color, testid }) => {
  const pendingCount = data.pending_count || 0;
  const pendingRev = data.pending_revenue || 0;
  return (
    <div
      className="bg-[#0A0A0A] border rounded p-3"
      style={{ borderColor: pendingCount > 0 ? color : '#1F1F1F' }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-[#9E9E9E]">{label}</p>
        <Icon size={16} style={{ color }} weight="duotone" />
      </div>
      <div className="flex items-baseline gap-3">
        <div>
          <p className="text-[9px] uppercase tracking-wider text-[#9E9E9E]">Total</p>
          <p className="text-xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
            {data.total_count || 0}
            <span className="text-xs text-[#9E9E9E] ml-1 font-normal">
              · ${Number(data.total_revenue || 0).toFixed(0)}
            </span>
          </p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-[9px] uppercase tracking-wider text-[#9E9E9E]">Unpaid</p>
          <p
            className="text-xl font-black"
            style={{ fontFamily: 'Outfit', color: pendingCount > 0 ? '#FF9500' : '#34C759' }}
            data-testid={`${testid}-pending`}
          >
            {pendingCount}
            <span className="text-xs ml-1 font-normal" style={{ color: pendingCount > 0 ? '#FF9500' : '#34C759' }}>
              · ${Number(pendingRev).toFixed(0)}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

const PaymentBadge = ({ status }) => {
  const styles = {
    PAID: { bg: 'bg-[#34C759]/15', fg: 'text-[#34C759]' },
    PENDING: { bg: 'bg-[#FACC15]/15', fg: 'text-[#FACC15]' },
    UNPAID: { bg: 'bg-[#FF3B30]/15', fg: 'text-[#FF3B30]' },
    COMP: { bg: 'bg-[#0096FF]/15', fg: 'text-[#0096FF]' },
    REFUNDED: { bg: 'bg-[#AF52DE]/15', fg: 'text-[#AF52DE]' },
  }[status] || { bg: 'bg-[#9E9E9E]/15', fg: 'text-[#9E9E9E]' };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${styles.bg} ${styles.fg}`}>
      {status || 'UNPAID'}
    </span>
  );
};
